// src/utils/checkinSorting.js
// Pure mapping from an open house check-in to CRM records. The Sort & Send
// UI decides WHICH of these to create; this module only builds payloads.
import { CORE_INTENTS } from "./checkinFlow";

export const suggestedSort = (intent) =>
  intent === "buy" || intent === "both" ? "buyer"
  : intent === "sell" ? "seller"
  : "skipped";

export const checkinDisplayName = (c) =>
  [String(c?.firstName || "").trim(), String(c?.lastName || "").trim()]
    .filter(Boolean)
    .join(" ");

// "YYYY-MM-DD" -> "MM/DD/YYYY" (house display standard). Junk passes through.
export function mmddyyyy(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(iso || "");
}

const intentLabel = (v) =>
  CORE_INTENTS.find((i) => i.value === v)?.label || String(v || "");

// Everything captured on the iPad, as lead notes. Nothing typed at the open
// house is ever lost, even when the guest is not saved as a contact.
export function checkinNotes(c, oh, template) {
  const lines = [];
  const addr = String(oh?.propertyAddress || "").split(",")[0].trim();
  lines.push(`Open house sign-in: ${addr || "unknown address"} on ${mmddyyyy(oh?.date)}`);
  if (c?.intent) lines.push(`Plans: ${intentLabel(c.intent)}`);
  if (Number(c?.partySize) > 0) lines.push(`Group of ${Number(c?.partySize)}`);
  const reach = [c?.phone, c?.email].map((v) => String(v || "").trim()).filter(Boolean);
  if (reach.length) lines.push(`Reach: ${reach.join(" / ")}`);
  if (c?.hasAgent) {
    const who = [
      String(c.agentName || "").trim(),
      c.agentBrokerage ? `(${String(c.agentBrokerage).trim()})` : "",
      String(c.agentPhone || "").trim(),
    ].filter(Boolean).join(" ");
    lines.push(`Working with agent: ${who || "yes"}`);
  }
  const questions = template?.questions || [];
  Object.entries(c?.answers || {}).forEach(([qid, val]) => {
    if (val === "" || val == null) return;
    const prompt = questions.find((q) => q?.id === qid)?.prompt || qid;
    lines.push(`${prompt}: ${val}`);
  });
  return lines.join("\n");
}

// Mirrors the FUB importer's sync_contacts shape (addContactToFirebase).
export function buildContactDoc(c, { id, userId }) {
  const phone = String(c?.phone || "").trim();
  const email = String(c?.email || "").trim();
  return {
    id,
    firstName: String(c?.firstName || "").trim(),
    lastName: String(c?.lastName || "").trim(),
    name: checkinDisplayName(c),
    phones: phone ? [{ value: phone, type: "mobile", primary: true }] : [],
    emails: email ? [{ value: email, type: "personal", primary: true }] : [],
    phone,
    email,
    source: "openhouse",
    createdBy: userId,
    createdAt: Date.now(),
  };
}

// Who a saved contact belongs to: the HOSTING agent when they're on the team
// (same rule leadCore uses for leads), else whoever is doing the sorting.
// The Contacts page only shows rows where createdBy == the viewer, so filing
// under the sorter would hide an agent's guests from her when someone else
// sorts on her behalf.
export function contactOwnerId(oh, sorterId) {
  return oh?.hostingAgentId || sorterId || "";
}

function leadCore(c, oh, { contactId, template, fallbackAgent }) {
  const assignedAgent = oh?.hostingAgentId || fallbackAgent?.id || "";
  const assignedAgentName = oh?.hostingAgentId
    ? oh?.hostingAgentName || ""
    : fallbackAgent?.name || "";
  return {
    contactId: contactId || "",
    contactName: checkinDisplayName(c),
    coClients: [],
    assignedAgent,
    assignedAgentName,
    stage: "New Lead",
    notes: checkinNotes(c, oh, template),
    openHouseId: c?.openHouseId || oh?.id || "",
    checkinId: c?.id || "",
    partySize: Number(c?.partySize) || 1,
  };
}

// Buyer pipeline stores the system source id in `leadSource`.
export function buildBuyerLead(c, oh, opts) {
  return { ...leadCore(c, oh, opts), leadSource: opts?.sourceId || "" };
}

// Listing pipeline stores it in `source`.
export function buildListingLead(c, oh, opts) {
  return { ...leadCore(c, oh, opts), source: opts?.sourceId || "" };
}

// System lead-source id for "Open House" (empty string when it doesn't exist
// yet; the caller then creates it via saveLeadSource and uses the new id).
export function findOpenHouseSource(sources) {
  const hit = (sources || []).find(
    (s) => s && String(s.name || "").trim().toLowerCase() === "open house",
  );
  return hit?.id || hit?.docId || "";
}
